import type { Company } from 'types';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useOrganizations } from '@/state/useOrganizations';
import { SearchInput } from '@/components/SearchInput';
import { Plus, Edit, Check, X, Trash2, Users } from 'lucide-react';
import { DataTable, useDataControl } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useAuth } from 'hooks/useAuth';
import { readApiError } from '@/state/billing/api-error';
import { ROW_ACTION_BUTTON, SEARCH_FIELD_CLASS } from '../ConsolePanel';
import { FirstRunState } from '../FirstRunState';
import { AddClientDialog } from './AddClientDialog';

export function ClientsScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const manager = useOrganizations();
  const { user } = useAuth();
  // Deleting a client ends what it serves, so it is an Owner/Admin action (403 for a Member).
  const canRemoveClients = !!user && user.role !== 'Member';
  const { filteredOrgs, organizations, options } = manager;
  const { toggleOrganization, setOrganization, changeFriendlyName } = manager;
  const { removeOrganization } = manager;
  const [editing, setEditing] = useState<number | null>(null);
  const [newFriendlyName, setNewFriendlyName] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [removing, setRemoving] = useState<Company | null>(null);
  const dataController = useDataControl<Company>(filteredOrgs || []);
  const { searchQuery, setSearchQuery } = dataController;

  useEffect(() => {
    const { state, pathname } = location;
    if (state?.create) {
      setIsCreating(true);
      navigate(pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  if (!filteredOrgs) return null;

  const handleEditName = (id: number, currentName: string) => {
    setEditing(id);
    setNewFriendlyName(currentName);
  };

  /**
   * An empty name used to close the editor and save nothing, so the rename looked applied
   * until the row redrew with the old name. The editor now stays open and says why.
   */
  const handleSaveEdit = async (id: number) => {
    if (!newFriendlyName?.trim()) {
      toast.error('Enter a name, or press Escape to keep the current one.');
      return;
    }
    await changeFriendlyName(id, newFriendlyName);
    setEditing(null);
    setNewFriendlyName(null);
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setNewFriendlyName(null);
  };

  /**
   * The dialog stays open on failure. The server refuses with a message when it
   * could not cancel a live subscription the company holds (502), and nothing
   * was deleted; that message is the one worth showing.
   */
  const handleRemoveClient = async () => {
    if (!removing) return;
    try {
      await removeOrganization(removing.id);
      toast.success(`${removing.friendlyName ?? 'The client'} was removed.`);
      setRemoving(null);
    } catch (error) {
      toast.error(readApiError(error).message ?? 'Could not remove the client.');
    }
  };

  return (
    <div className="flex flex-col gap-[14px]">
      <AddClientDialog open={isCreating} onClose={() => setIsCreating(false)} />

      <ConfirmDialog
        open={!!removing}
        title="Remove client"
        confirmLabel="Remove"
        confirmVariant="destructive"
        onConfirm={handleRemoveClient}
        onCancel={() => setRemoving(null)}
      >
        <p>
          This permanently removes <strong>{removing?.friendlyName}</strong> from your account. This cannot be undone.
        </p>
      </ConfirmDialog>
      <section className="bg-panel border-line rounded-panel border">
        {/* Stacked below `md`: the title, the search and a three-word button do not
            share one 375px line. */}
        <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
          <h2 className="text-foreground m-0 text-body font-medium">Clients</h2>
          <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">
            {/* SearchInput collapses to a magnifier below `md` and drops its field in
                absolutely, so it needs a positioned parent of the field's width. */}
            <div className="relative w-full md:w-auto">
              <SearchInput
                placeholder="Search organizations..."
                value={searchQuery}
                handleSearch={setSearchQuery}
                className={SEARCH_FIELD_CLASS}
              />
            </div>
            <Button
              onClick={() => setIsCreating(true)}
              className="h-9 w-full rounded-lg md:w-auto"
              data-tour="clients-create"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create New Client
            </Button>
          </div>
        </div>

        <div className="p-3" data-tour="clients-list">
          {filteredOrgs && dataController.rawData?.length === 0 ? (
            <FirstRunState
              icon={Users}
              title="No clients yet"
              description="A client is one of the companies you run tests for. Each has its own websites, results and plan, so a client is billed as its own plan on your account. Your own website is included in the agency fee."
              action={
                <Button onClick={() => setIsCreating(true)} className="h-9 rounded-lg">
                  <Plus className="mr-2 h-4 w-4" />
                  Add your first client
                </Button>
              }
            />
          ) : (
            <DataTable
              dataController={dataController}
              searchKeys={['friendlyName']}
              pageSize={10}
              noDataMessage="No clients found."
              noDataCTA={
                <Button onClick={() => setIsCreating(true)} className="mt-4 h-9 w-full rounded-lg min-[500px]:w-auto">
                  <Plus className="mr-2 h-4 w-4" />
                  Create your first Client
                </Button>
              }
              columns={[
                {
                  field: 'id',
                  label: ' ',
                  headerClassName: 'micro-label w-20',
                  // A bare "Select" button with no head: on a card it belongs in the
                  // footer with the other controls, not in a labelless body row.
                  mobile: 'action',
                  // The first row's controls carry the product tour's anchors.
                  format: ({ value, idx }) => (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOrganization(value as number)}
                      data-tour={idx === 0 ? 'clients-select' : undefined}
                      className={ROW_ACTION_BUTTON}
                    >
                      Select
                    </Button>
                  ),
                },
                {
                  field: 'friendlyName',
                  label: 'Name',
                  sortKey: 'friendlyName',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  mobile: 'title',
                  format: ({ value, item, idx }) => {
                    if (editing === item.id) {
                      return (
                        <div className="flex items-center gap-2">
                          <Input
                            value={newFriendlyName || ''}
                            onChange={(ev) => setNewFriendlyName(ev.target.value)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter') {
                                ev.preventDefault();
                                handleSaveEdit(item.id);
                              } else if (ev.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            className="border-line bg-field text-foreground h-8 rounded-lg text-body"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveEdit(item.id)}
                            aria-label="Save client name"
                            title="Save client name"
                            className="text-dim hover:text-ok hover:bg-hover h-7 w-7 rounded-lg p-0"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            aria-label="Cancel renaming client"
                            title="Cancel renaming client"
                            className="text-dim hover:text-foreground hover:bg-hover h-7 w-7 rounded-lg p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-foreground block max-w-[220px] truncate" title={value as string}>
                          {value as string}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditName(item.id, item.friendlyName ?? '')}
                          disabled={editing !== null}
                          data-tour={idx === 0 ? 'clients-rename' : undefined}
                          // Every rename button in the table greys out while any one row
                          // is open. With the open editor scrolled out of view the whole
                          // column just looked broken, so the reason is stated.
                          aria-label={`Rename ${value as string}`}
                          title={
                            editing !== null
                              ? 'Finish or cancel the name you are already editing first'
                              : `Rename ${value as string}`
                          }
                          className="text-dim hover:text-foreground hover:bg-hover h-7 w-7 rounded-lg border-none bg-transparent p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  },
                },
                {
                  field: 'size',
                  label: 'Size',
                  sortKey: 'size',
                  headerClassName: 'micro-label w-32',
                  cellClassName: 'num text-dim',
                  mobile: 'meta',
                },
                {
                  field: 'active',
                  label: 'Active',
                  headerClassName: 'micro-label w-32',
                  mobile: 'meta',
                  format: ({ value, item, idx }) => (
                    <Switch
                      checked={value as boolean}
                      onCheckedChange={toggleOrganization(item)}
                      data-tour={idx === 0 ? 'clients-active' : undefined}
                    />
                  ),
                },
                {
                  label: ' ',
                  headerClassName: 'micro-label w-16',
                  condition: canRemoveClients,
                  // Same reasoning as the Select button: a labelless icon control
                  // belongs in the card footer below `md`, not in a body row.
                  mobile: 'action',
                  format: ({ item, idx }) => (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRemoving(item)}
                      data-tour={idx === 0 ? 'clients-remove' : undefined}
                      aria-label={`Remove ${item.friendlyName ?? 'client'}`}
                      title={`Remove ${item.friendlyName ?? 'client'}`}
                      className="text-dim hover:text-danger hover:bg-hover h-7 w-7 rounded-lg p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </div>
      </section>
    </div>
  );
}
